import { Routes } from "@angular/router";
import { LoginComponent } from "./auth/login/login.component";
import { ForgetPasswordComponent } from "./auth/forget-password/forget-password.component";
import { resetPasswordGuard } from "./auth/guards/reset-password.guard";
import { ResetPasswordComponent } from "./auth/reset-password/reset-password.component";
import { guestGuard } from "./auth/guards/guest.guard";
import { AuthGuard } from "./auth/guards/auth.guard";
import { mfaGuard } from "./auth/guards/mfa.guard";
import { licenceGuard } from "./auth/guards/licence.guard";
import { dataSyncCanDeactivateGuard, dataSyncGuard } from "./auth/guards/data-sync.guard";
import { syncStatusGuard } from "./auth/guards/sync-status.guard";
import { chatAccessGuard } from "./auth/guards/chat-access.guard";
import { LicenceComponent } from "./auth/licence/licence.component";
import { VerifyLicenceComponent } from "./auth/verify-licence/verify-licence.component";
import { AutheticateComponent } from "./auth/autheticate/autheticate.component";
import { DataSyncComponent } from "./auth/data-sync/data-sync.component";

export const routes: Routes = [
  {
    path: 'login', component: LoginComponent, pathMatch: 'full',
    canActivate: [guestGuard]
  },
  {
    path: 'forgot-password', component: ForgetPasswordComponent,
    canActivate: [guestGuard]
  },
  {
    path: 'reset-password',
    component: ResetPasswordComponent,
    canActivate: [resetPasswordGuard]
  },
  {
    path: 'verify-licence',
    component: VerifyLicenceComponent,
    canActivate: [licenceGuard]
  },
  {
    path: 'authenticate',
    component: AutheticateComponent,
    canActivate: [mfaGuard],
    data: { hideLayout: true }
  },
  {
    path: 'data-sync',
    component: DataSyncComponent,
    data: { hideLayout: true },
    canActivate: [dataSyncGuard],
    canDeactivate: [dataSyncCanDeactivateGuard],
  },
  { path: "", redirectTo: "/load-planning", pathMatch: "full" },
  {
    path: "load-planning",
    canActivate: [AuthGuard, syncStatusGuard],
    children: [
      { path: "", redirectTo: "flight-list", pathMatch: "full" }, // Redirect to flight-list
      {
        path: "flight-list",
        loadComponent: () => import("./features/flight-list/flight-list.component").then(m => m.FlightListComponent)
      },
      {
        path: "chat",
        canActivate: [chatAccessGuard],
        loadComponent: () => import("./features/chat/chat.component").then(m => m.ChatComponent)
      },
      {
        path: "plan",
        loadComponent: () => import("./features/planning-stage/planning-stage.component").then(m => m.PlanningStageComponent),
        children: [
          { path: "", redirectTo: "manage-payload", pathMatch: "full" },
          {
            path: "manage-payload",
            loadComponent: () => import("./features/payload/manage-payload/manage-payload.component").then(m => m.ManagePayloadComponent)
          },
          {
            path: "manual-load-planning",
            loadComponent: () => import("./features/manual-load-planning/manual-load-planning.component").then(m => m.ManualLoadPlanningComponent)
          },
          {
            path: "manage-fuel",
            loadComponent: () => import("./features/manage-fuel/manage-fuel.component").then(m => m.ManageFuelComponent)
          },
          {
            path: "assess-load-plan",
            loadComponent: () => import("./features/asses-load-plan/assess-load-plan/assess-load-plan.component").then(m => m.AssessLoadPlanComponent)
          },
          {
            path: "ld-sq-assignment",
            loadComponent: () => import("./features/ld-sq/ld-sq.component").then((m) => m.LdSqComponent)
          },
        ],
      },
    ],
  },
  {
    path: "reports",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./features/reports/reports.component").then(
        (m) => m.ReportsComponent
      ),
  },
  {
    path: "master-management",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./features/master-management/master-management.component").then(
        (m) => m.MasterManagementComponent
      ),
      children: [
        {
          path: "user-management/user-list",
          loadComponent: () =>
            import("./features/master-management/user-management/user-list/user-list.component").then(
              (m) => m.UserListComponent
            ),
        },
        {
          path: "license-management",
          loadComponent: () =>
            import("./features/master-management/license-management/license-management.component").then(
              (m) => m.LicenseManagementComponent
            ),
        },
        {
          path: "airline-management/airline-list",
          loadComponent: () =>
            import("./features/master-management/airline-management/airline-list/airline-list.component").then(
              (m) => m.AirlineListComponent
            ),
        },
        {
          path: "airline-management/configure-checklist",
          loadComponent: () =>
            import("./features/master-management/airline-management/configure-checklist/configure-checklist.component").then(
              (m) => m.ConfigureChecklistComponent
            ),
        },
        {
          path: "airline-management/configure-chat",
          loadComponent: () =>
            import("./features/master-management/airline-management/chat-config/chat-config.component").then(
              (m) => m.ChatConfigComponent
            ),
        },
        {
          path: "aircraft-management",
          children: [
            {
              path: 'aircraft-list',
              loadComponent: () =>
                  import("./features/master-management/aircraft-management/aircraft-list/aircraft-list.component").then(
                    (m) => m.AircraftListComponent
                  ),
            },
            {
              path: "create-aircraft",
              loadComponent: () =>
                import("./features/master-management/aircraft-management/create-aircraft/create-aircraft.component").then(
                  (m) => m.CreateAircraftComponent
                ),
                children: [
                  { path: "", pathMatch: "full", redirectTo: "aircraft-registration" },
                  {
                    path: "aircraft-registration",
                    data: { hideCreateAircraftHeader: true },
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/aircraft-registration/aircraft-registration.component").then(
                        (m) => m.AircraftRegistrationComponent
                      ),
                  },
                  {
                    path: "aircraft-configuration",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/aircraft-configuration/aircraft-configuration.component").then(
                        (m) => m.AircraftConfigurationComponent
                      ),
                  },
                  {
                    path: "aircraft-deck",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/aircraft-deck/aircraft-deck.component").then(
                        (m) => m.AircraftDeckComponent
                      ),
                  },
                  {
                    path: "deck-compartments",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/deck-compartments/deck-compartments.component").then(
                        (m) => m.DeckCompartmentsComponent
                      ),
                  },
                  {
                    path: "aircraft-zone",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/aircraft-zone/aircraft-zone.component").then(
                        (m) => m.AircraftZoneComponent
                      ),
                  },
                  {
                    path: "position-template",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/position-template/position-template.component").then(
                        (m) => m.PositionTemplateComponent
                      ),
                  },
                  {
                    path: "position-envelope",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/position-envelope/position-envelope.component").then(
                        (m) => m.PositionEnvelopeComponent
                      ),
                  },
                  {
                    path: "position-weight-limits",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/position-weight-limits/position-weight-limits.component").then(
                        (m) => m.PositionWeightLimitsComponent
                      ),
                  },
                  {
                    path: "unsymmetrical-linearload-limits",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/unsymmetrical-linearload-limits/unsymmetrical-linearload-limits.component").then(
                        (m) => m.UnsymmetricalLinearloadLimitsComponent
                      ),
                  },
                  {
                    path: "combined-limits",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/combined-limits/combined-limits.component").then(
                        (m) => m.CombinedLimitsComponent
                      ),
                  },
                  {
                    path: 'lateral-imbalance/:mode',
                    loadComponent: () =>
                      import(
                        './features/master-management/aircraft-management/create-aircraft/components/aircraft-lateral/aircraft-lateral.component'
                      ).then(m => m.AircraftLateralComponent),
                  },
                  {
                    path: "cg-graph",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/cg-graph/cg-graph.component").then(
                        (m) => m.CgGraphComponent
                      ),
                  },
                  {
                    path: "cg-graph",
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/cg-graph/cg-graph.component").then(
                        (m) => m.CgGraphComponent
                      ),
                  },
                  {
                    path: 'center-of-gravity-limits',
                    children: [
                      {
                        path: '',
                        loadComponent: () => (
                          import('./features/master-management/aircraft-management/create-aircraft/components/aircraft-cog-limit/aircraft-cog-limit.component')
                        ).then(m => m.AircraftCogLimitComponent)
                      },
                      {
                        path: "edit",
                        loadComponent: () => (
                          import('./features/master-management/aircraft-management/create-aircraft/components/aircraft-cog-limit/component/edit-aircrft-cog/edit-aircrft-cog.component')
                        ).then(m => m.EditAircrftCogComponent)
                      }
                    ]
                  },
                  {
                    path: 'door-config',
                    loadComponent: () =>
                      import("./features/master-management/aircraft-management/create-aircraft/components/aircraft-additional-config/aircraft-additional-config.component").then(
                        (m) => m.AircraftAdditionalConfigComponent
                      ),
                  }
                ],
            },
          ],
        },
        {
          path: "uld-management/uld-list",
          loadComponent: () =>
            import("./features/master-management/uld-management/uld-list/uld-list.component").then(
              (m) => m.UldListComponent
            ),
        },
        {
          path: "airport-management/airport-list",
          loadComponent: () =>
            import("./features/master-management/airport-management/airport-list/airport-list.component").then(
              (m) => m.AirportListComponent
            ),
        },
        {
          path: "contour-management/contour-list",
          loadComponent: () =>
            import("./features/master-management/contour-management/contour-list/contour-list.component").then(
              (m) => m.ContourListComponent
            ),
        },
        {
        path: "airline-management/stagewise-tat",
        loadComponent: () =>
          import("./features/master-management/stagewise-tat/stagewise-tat.component").then(
            (m) => m.StagewiseTatComponent
          ),
      },
      {
        path: "conflict-management/conflict-management-list",
        loadComponent: () =>
          import("./features/master-management/conflict-management/conflict-management-list/conflict-management-list.component").then(
            (m) => m.ConflictManagementListComponent
          ),
      }
      ]
  },
  {
    path: "load-master",
    canActivate: [AuthGuard],
    loadComponent: () =>
      import("./features/load-master/load-master.component").then(
        (m) => m.LoadMasterComponent
      ),
    children: [
      {
        path: 'load-master-report', loadComponent: () => import('./features/load-master/load-master-report/load-master-report.component')
          .then(m => m.LoadMasterReportComponent)
      }
    ]
  },
  { path: '**', redirectTo: 'login' }
];
